import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';
import API from '@Services/apiAxios';

interface AnalyticsStatsResponse {
  totalCount: number;
  cancelled: number;
  picked_up: number;
}

const TodayShipment = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [system, setSystem] = useState<string[]>([]);
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsStatsResponse | null>(null);
  // const [loading, setLoading] = useState<boolean>(true);

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, []);

  const fetchAnalyticsStats = async () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await API.get('/analytics/shipment/user/today', {
        params: {
          date: currentDate,
          'system[]': system,
        },
      });
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics stats:', error);
    }
  };

  useEffect(() => {
    fetchAnalyticsStats();
  }, [system]);

  return (
    <Box
      sx={{
        borderRadius: '9px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '80px', sm: '40px' },
        padding: '20px 40px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            marginBottom: { xs: '10px', sm: '0' },
          }}
        >
          <FilterSelect
            id="system"
            label="Select system"
            options={loggedInUserSystemDropdownList}
            value={system}
            onChange={onSystemFilter}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 3, md: 0 },
          mb: 4,
        }}
      >
        <Box
          sx={{
            flexBasis: { xs: '100%', md: '20%' },
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '20px', md: '24px' },
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px',
                display: { xs: 'block', md: 'none' },
              },
            }}
          >
            Today's Shipments
          </Typography>
        </Box>

        <Box
          sx={{
            flexBasis: { xs: '100%', md: '80%' },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 2, md: 4 },
            alignItems: 'stretch',
          }}
        >
          {/* Active Shipments Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                borderColor: 'primary.light',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                src="/images/icons/AdminHome/shipment.png"
                alt="active shipments"
                style={{ width: '40px', height: '38px' }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  color: 'primary.main',
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {analyticsData?.totalCount ?? 0}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Active Shipments
              </Typography>
            </Box>
          </Box>

          {/* Canceled Shipments Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                borderColor: 'error.light',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                src="/images/icons/AdminHome/shipment.png"
                alt="canceled shipments"
                style={{
                  width: '40px',
                  height: '38px',
                  filter:
                    'brightness(0) saturate(100%) invert(36%) sepia(91%) saturate(1511%) hue-rotate(332deg) brightness(94%) contrast(93%)',
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  color: 'error.main',
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {analyticsData?.cancelled ?? 0}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Canceled
              </Typography>
            </Box>
          </Box>

          {/* Picked Up Shipments Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                borderColor: 'success.light',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                src="/images/icons/AdminHome/shipment.png"
                alt="picked up shipments"
                style={{
                  width: '40px',
                  height: '38px',
                  filter:
                    'brightness(0) saturate(100%) invert(36%) sepia(91%) saturate(1511%) hue-rotate(106deg) brightness(94%) contrast(93%)',
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  color: 'success.main',
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {analyticsData?.picked_up ?? 0}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Picked Up
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TodayShipment;
